import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";
import { Button, Input, ActionButton } from './common';
import { toast } from 'react-toastify';
import ClientDataService from "../services/CustomerService";
import log from "../services/logger";

const ClientsList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({
    clientName: "",
    clientCode: "",
  });
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);

  const retrieveClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ClientDataService.getAll();
      if (response && Array.isArray(response.data)) {
        const sortedClients = response.data.sort((a, b) => 
          a.isActive === b.isActive ? 0 : a.isActive ? 1 : -1
        );
        setClients(sortedClients);
      }
    } catch (error) {
      toast.error('Greška pri učitavanju klijenata');
      log.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    retrieveClients();
  }, [retrieveClients]);

  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const nameMatches = !searchParams.clientName || 
        client.nameOfTheLegalEntity.toLowerCase().includes(searchParams.clientName.toLowerCase());
      const codeMatches = !searchParams.clientCode || 
        (client.customerCode && client.customerCode.toLowerCase().includes(searchParams.clientCode.toLowerCase()));
      return nameMatches && codeMatches;
    });
  }, [clients, searchParams]);

  const handleSearch = () => {
    // The filtering is handled by the useMemo above
    // This is just to trigger a re-render if needed
    setSearchParams({ ...searchParams });
  };

  const columns = useMemo(() => [
    {
      Header: 'Šifra Klijenta',
      accessor: 'customerCode',
      width: '120px',
    },
    {
      Header: 'Naziv',
      accessor: 'nameOfTheLegalEntity',
      width: '200px',
    },
    {
      Header: 'Grad',
      accessor: 'city',
      width: '120px',
    },
    {
      Header: 'PIB',
      accessor: 'pib',
      width: '120px',
    },
    {
      Header: 'Kontakt Osoba',
      accessor: 'contactPerson',
      width: '150px',
    },
    {
      Header: 'Email',
      accessor: 'email',
      width: '180px',
    },
    {
      Header: 'Status',
      accessor: 'isActive',
      width: '100px',
      Cell: ({ value }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Aktivan' : 'Neaktivan'}
        </span>
      ),
    },
    {
      Header: 'Akcije',
      id: 'actions',
      width: 'auto',
      Cell: ({ row }) => (
        <div className="flex items-center gap-2 min-w-[200px] whitespace-nowrap">
          <ActionButton
            onClick={() => navigate(`/clients/edit/${row.original.customer_id}`)}
            icon="fa-edit"
            label="Izmeni klijenta"
            variant="primary"
            className="opacity-100"
          />
          <ActionButton
            onClick={() => navigate(`/visibleArticles`, { state: { clientID: row.original.customer_id } })}
            icon="fa-eye"
            label="Vidljivi artikli"
            variant="primary"
            className="opacity-100"
          />
        </div>
      ),
    },
  ], [navigate]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    nextPage,
    previousPage,
    setPageSize: setTablePageSize,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: filteredClients,
      initialState: { pageSize }
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Search Section */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Naziv Klijenta
            </label>
            <Input
              type="text"
              value={searchParams.clientName}
              onChange={(e) => setSearchParams(prev => ({ ...prev, clientName: e.target.value }))}
              placeholder="Unesite naziv klijenta..."
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Šifra Klijenta
            </label>
            <Input
              type="text"
              value={searchParams.clientCode}
              onChange={(e) => setSearchParams(prev => ({ ...prev, clientCode: e.target.value }))}
              placeholder="Unesite šifru klijenta..."
              className="w-full"
            />
          </div>
        </div>
        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => setSearchParams({ clientName: "", clientCode: "" })}
            variant="secondary"
          >
            Resetuj
          </Button>
          <Button
            onClick={handleSearch}
            variant="primary"
          >
            Pretraži
          </Button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
                <thead>
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-100">
                      {headerGroup.headers.map(column => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors"
                          style={{ width: column.width }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{column.render('Header')}</span>
                            <span className="text-gray-400">
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <i className="fas fa-sort-down ml-1" />
                                ) : (
                                  <i className="fas fa-sort-up ml-1" />
                                )
                              ) : (
                                <i className="fas fa-sort ml-1 opacity-30" />
                              )}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                  {page.map(row => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className={`hover:bg-blue-50 transition-colors ${
                          !row.original.isActive ? 'bg-red-50' : ''
                        }`}
                      >
                        {row.cells.map(cell => (
                          <td
                            {...cell.getCellProps()}
                            className="px-6 py-4 text-sm text-gray-900 overflow-visible"
                          >
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {page.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
                        Nema pronađenih klijenata
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Section - Updated Styling */}
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 bg-gray-50">
              <div className="hidden sm:flex sm:items-center">
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setTablePageSize(Number(e.target.value));
                  }}
                  className="form-select rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                >
                  {[10, 20, 30, 40, 50].map(size => (
                    <option key={size} value={size}>
                      {size} redova
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-700 ml-4">
                  Prikazano <span className="font-medium">{pageIndex * pageSize + 1}</span> -{" "}
                  <span className="font-medium">{Math.min((pageIndex + 1) * pageSize, filteredClients.length)}</span> od{" "}
                  <span className="font-medium">{filteredClients.length}</span> rezultata
                </p>
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  variant="secondary"
                  className="px-4 py-2 text-sm rounded-md disabled:opacity-50"
                >
                  <i className="fas fa-chevron-left mr-1"></i>
                  Prethodno
                </Button>
                <Button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  variant="secondary"
                  className="px-4 py-2 text-sm rounded-md disabled:opacity-50"
                >
                  Sledeće
                  <i className="fas fa-chevron-right ml-1"></i>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Client Button */}
      <div className="mt-6 flex justify-end">
        <Button
          onClick={() => navigate('/clients/add')}
          variant="primary"
          className="inline-flex items-center"
        >
          <i className="fas fa-plus mr-2"></i>
          Dodaj Klijenta
        </Button>
      </div>
    </div>
  );
};

export default ClientsList;
